// src/components/ui/MobiliarioForm.jsx
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, Armchair, MapPin, Tag, Palette, ClipboardList, LoaderPinwheel } from "lucide-react";
import SelectField from "./SelectField";
import InputField from "./InputField";
import TextAreaField from "./TextAreaField";
import {
  TIPOS_MOBILIARIO,
  ESTADOS_MOBILIARIO,
  PROPIEDADES_MOBILIARIO,
  toOptions,
} from "../../constants/mobiliario";

const VACIO = {
  cliente: "",
  sede: "",
  area: "",
  tipo: "",
  descripcion: "",
  marca: "",
  modelo: "",
  serial: "",
  inventario: "",
  material: "",
  color: "",
  dimensiones: "",
  estado: "Bueno",
  propiedad: "Propio",
  responsable: "",
  proveedor: "",
  fecha_compra: "",
  costo: "",
  observaciones: "",
};

const idDe = (v) => v?._id || v || "";

const Seccion = ({ icon: Icon, title, children }) => (
  <section className="space-y-3">
    <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
      <Icon className="h-3.5 w-3.5" /> {title}
    </h3>
    {children}
  </section>
);

const MobiliarioForm = ({ item = null, tipos = TIPOS_MOBILIARIO, onClose, onSave }) => {
  const [form, setForm] = useState(VACIO);
  const [clientes, setClientes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [saving, setSaving] = useState(false);

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const get = useCallback(
    (path) => axios.get(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } }),
    [API, token],
  );

  const fetchSedes = useCallback(
    async (clienteId) => {
      if (!clienteId) return setSedes([]);
      try {
        setSedes((await get(`/sedes/${clienteId}`)).data || []);
      } catch {
        toast.error("Error cargando sedes");
      }
    },
    [get],
  );

  const fetchModelos = useCallback(
    async (marcaId) => {
      if (!marcaId) return setModelos([]);
      try {
        setModelos((await get(`/modelos/${marcaId}`)).data || []);
      } catch {
        toast.error("Error cargando modelos");
      }
    },
    [get],
  );

  useEffect(() => {
    Promise.all([get("/clientes"), get("/areas"), get("/marcas"), get("/prov")])
      .then(([c, a, m, p]) => {
        setClientes(c.data || []);
        setAreas(a.data || []);
        setMarcas(m.data || []);
        setProveedores(p.data || []);
      })
      .catch(() => toast.error("Error cargando datos iniciales"));
  }, [get]);

  // Modo edición: precargar valores y dependientes
  useEffect(() => {
    if (!item) return;
    setForm({
      ...VACIO,
      ...Object.fromEntries(
        Object.keys(VACIO).map((k) => [k, item[k] ?? VACIO[k]]),
      ),
      cliente: idDe(item.cliente),
      sede: idDe(item.sede),
      area: idDe(item.area),
      marca: idDe(item.marca),
      modelo: idDe(item.modelo),
      proveedor: idDe(item.proveedor),
      fecha_compra: item.fecha_compra ? item.fecha_compra.slice(0, 10) : "",
      costo: item.costo ?? "",
    });
    fetchSedes(idDe(item.cliente));
    fetchModelos(idDe(item.marca));
  }, [item, fetchSedes, fetchModelos]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "cliente") {
        next.sede = "";
        fetchSedes(value);
      }
      if (name === "marca") {
        next.modelo = "";
        fetchModelos(value);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const faltantes = [
      ["cliente", "Cliente"],
      ["sede", "Sede"],
      ["area", "Área"],
      ["tipo", "Tipo"],
    ].filter(([k]) => !String(form[k] || "").trim());
    if (faltantes.length) {
      toast.warning(`Completa: ${faltantes.map(([, l]) => l).join(", ")}`);
      return;
    }

    setSaving(true);
    try {
      await onSave({ ...form, tipo: form.tipo.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-xl">
              <Armchair className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {item ? "Editar elemento" : "Nuevo elemento de mobiliario"}
              </h2>
              <p className="text-xs text-gray-500">
                {item ? `Código ${item.codigo}` : "El código y el QR se generan automáticamente"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 overflow-y-auto">
          <Seccion icon={MapPin} title="Ubicación">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SelectField label="Cliente *" name="cliente" value={form.cliente} onChange={handleChange} options={clientes} />
              <SelectField
                label="Sede *"
                name="sede"
                value={form.sede}
                onChange={handleChange}
                options={sedes}
                isDisabled={!form.cliente}
                placeholder={form.cliente ? "Seleccionar..." : "Elige un cliente"}
              />
              <SelectField label="Área de servicio *" name="area" value={form.area} onChange={handleChange} options={areas} />
            </div>
          </Seccion>

          <Seccion icon={Tag} title="Identificación">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <InputField
                  label="Tipo *"
                  name="tipo"
                  value={form.tipo}
                  onChange={handleChange}
                  list="tipos-mobiliario"
                  placeholder="Silla, mesa, escritorio…"
                  autoComplete="off"
                />
                <datalist id="tipos-mobiliario">
                  {tipos.map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </div>
              <SelectField label="Marca" name="marca" value={form.marca} onChange={handleChange} options={marcas} />
              <SelectField
                label="Modelo"
                name="modelo"
                value={form.modelo}
                onChange={handleChange}
                options={modelos}
                isDisabled={!form.marca}
                placeholder={form.marca ? "Seleccionar..." : "Elige una marca"}
              />
              <InputField label="Serial" name="serial" value={form.serial} onChange={handleChange} placeholder="NR si no registra" />
              <InputField label="N° inventario" name="inventario" value={form.inventario} onChange={handleChange} />
              <InputField label="Descripción" name="descripcion" value={form.descripcion} onChange={handleChange} placeholder="Ej: silla interlocutora" />
            </div>
          </Seccion>

          <Seccion icon={Palette} title="Características">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <InputField label="Material" name="material" value={form.material} onChange={handleChange} placeholder="Madera, metal…" />
              <InputField label="Color" name="color" value={form.color} onChange={handleChange} />
              <InputField label="Dimensiones" name="dimensiones" value={form.dimensiones} onChange={handleChange} placeholder="Alto x ancho x fondo" />
            </div>
          </Seccion>

          <Seccion icon={ClipboardList} title="Estado y adquisición">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SelectField label="Estado" name="estado" value={form.estado} onChange={handleChange} options={toOptions(ESTADOS_MOBILIARIO)} />
              <SelectField label="Propiedad" name="propiedad" value={form.propiedad} onChange={handleChange} options={toOptions(PROPIEDADES_MOBILIARIO)} />
              <InputField label="Responsable" name="responsable" value={form.responsable} onChange={handleChange} />
              <SelectField label="Proveedor" name="proveedor" value={form.proveedor} onChange={handleChange} options={proveedores} />
              <InputField label="Fecha de compra" name="fecha_compra" type="date" value={form.fecha_compra} onChange={handleChange} />
              <InputField label="Costo" name="costo" type="number" min="0" value={form.costo} onChange={handleChange} />
            </div>
            <TextAreaField label="Observaciones" name="observaciones" value={form.observaciones} onChange={handleChange} />
          </Seccion>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
          >
            {saving && <LoaderPinwheel className="h-4 w-4 animate-spin" />}
            {saving ? "Guardando..." : item ? "Actualizar" : "Crear"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MobiliarioForm;
